export interface ProjectPhase {
  id: number;
  title: string;
  icon: string;
  color: string;
  tabs: string[];
}

export const PROJECT_PHASES: ProjectPhase[] = [
  {
    id: 1,
    title: "Project Preparation",
    icon: "📋",
    color: "#3b82f6", // blue
    tabs: [
      "Customer Details",
      "Project Measurements",
      "Estimate",
      "Notes",
      "Documents",
    ],
  },
  {
    id: 2,
    title: "Installation",
    icon: "🔨",
    color: "#f59e0b", // orange
    tabs: ["Materials", "Contractors", "Installation Details", "Payment Details"],
  },
  {
    id: 3,
    title: "Finalization",
    icon: "✅",
    color: "#10b981", // green
    tabs: ["Invoice", "Affidavit", "COC"],
  },
];

// Settings is separate from phases
export const SETTINGS_TAB = "Settings";

// Map from UI tab label to API ProjectStage enum name
export const TAB_TO_STAGE: Record<string, string> = {
  "Customer Details": "CUSTOMER_DETAILS",
  "Project Measurements": "PROJECT_MEASUREMENTS",
  "Materials": "MATERIALS",
  "Contractors": "CONTRACTORS",
  "Notes": "NOTES",
  "Documents": "DOCUMENTS",
  "Estimate": "ESTIMATE",
  "Installation Details": "INSTALLATION_DETAILS",
  "Payment Details": "PAYMENT_DETAILS",
  "Invoice": "INVOICE",
  "Affidavit": "AFFIDAVIT",
  "COC": "COC",
};

export const getStageNameForTab = (tabLabel: string): string | null =>
  TAB_TO_STAGE[tabLabel] ?? null;

// Stages controlled by the backend automatically (not clickable by admin)
export const AUTOMATED_STAGES = new Set([
  "Estimate",
  "Installation Details",
]);

/**
 * Get the phase for a given tab label
 */
export const getPhaseForTab = (tabLabel: string): ProjectPhase | null => {
  for (const phase of PROJECT_PHASES) {
    if (phase.tabs.includes(tabLabel)) {
      return phase;
    }
  }
  return null;
};

/**
 * Get the active phase index based on current tab
 */
export const getActivePhaseIndex = (tabLabel: string): number => {
  const phase = getPhaseForTab(tabLabel);
  return phase ? phase.id - 1 : 0;
};

/**
 * Check if a tab has required data (for completion indicators)
 * This is a client-side check for demo purposes
 */
export const checkTabCompletion = (
  tabLabel: string,
  projectData: any
): boolean => {
  if (!projectData) return false;

  switch (tabLabel) {
    case "Customer Details":
      return Boolean(
        projectData.customerDetails?.firstName &&
          projectData.customerDetails?.lastName &&
          projectData.customerDetails?.email
      );
    case "Project Measurements":
      return Boolean(projectData.insuranceScope);
    case "Materials":
      return Boolean(projectData.materials && projectData.materials.length > 0);
    case "Contractors":
      return Boolean(projectData.materials && projectData.materials.length > 0);
    case "Notes":
      return true; // Always show as complete (optional field)
    case "Documents":
      return Boolean(projectData.documents && projectData.documents.length > 0);
    case "Estimate":
      return Boolean(projectData.estimate && projectData.estimate.length > 0);
    case "Installation Details":
      return Boolean(projectData.installationDetails);
    case "Payment Details":
      return Boolean(
        projectData.paymentDetails && projectData.paymentDetails.length > 0
      );
    case "Invoice":
      return Boolean(projectData.documents?.some((doc: any) => doc.fileType === "INVOICE"));
    case "Affidavit":
      return Boolean(projectData.documents?.some((doc: any) => doc.fileType === "AFFIDAVIT"));
    case "COC":
      return Boolean(projectData.cocAndInvoice && projectData.cocAndInvoice.length > 0);
    default:
      return false;
  }
};

/**
 * Calculate phase completion percentage
 */
export const getPhaseCompletion = (
  phase: ProjectPhase,
  projectData: any,
  filteredTabs: string[]
): number => {
  const phaseTabs = phase.tabs.filter((tab) => filteredTabs.includes(tab));
  if (phaseTabs.length === 0) return 0;

  const completedTabs = phaseTabs.filter((tab) =>
    checkTabCompletion(tab, projectData)
  );
  return Math.round((completedTabs.length / phaseTabs.length) * 100);
};

/**
 * Get project status based on completion
 */
export const getProjectStatus = (
  projectData: any,
  filteredTabs: string[]
): { label: string; color: string } => {
  const phase1Completion = getPhaseCompletion(PROJECT_PHASES[0], projectData, filteredTabs);
  const phase2Completion = getPhaseCompletion(PROJECT_PHASES[1], projectData, filteredTabs);
  const phase3Completion = getPhaseCompletion(PROJECT_PHASES[2], projectData, filteredTabs);

  if (phase3Completion === 100) {
    return { label: "Completed", color: "#10b981" };
  } else if (phase2Completion > 0) {
    return { label: "In Progress", color: "#f59e0b" };
  } else {
    return { label: "Active", color: "#3b82f6" };
  }
};
